// Guided tour: spotlight on real interface elements, one step at a time. Optional and repeatable.
import { api } from "./api.ts";
import { icon } from "./icons.ts";
import { esc } from "./ui.ts";
import { go } from "./main.ts";

type Step = { title: string; text: string; targets?: string[]; view?: string; adminOnly?: boolean };

const STEPS: Step[] = [
  { title: "Esto es Vera", text: "Tu asistente personal. Le pides cosas como se las pedirías a una persona y se ocupa del correo, la agenda, las reservas y los viajes. Este recorrido dura un minuto; puedes salir cuando quieras." },
  { view: "chat", targets: ["[data-form]"], title: "Escríbele o háblale", text: "Escribe aquí o pulsa el micrófono para mandar una nota de voz. Responde siempre por escrito." },
  { view: "chat", targets: [".suggestions"], title: "Ideas para empezar", text: "Toca una sugerencia para verla en acción: el resumen de la mañana, preparar una reunión, organizar un viaje…" },
  { view: "chat", title: "Antes de actuar, te pregunta", text: "Para pagar, reservar, enviar algo a terceros o aceptar condiciones, te enseña una tarjeta con los datos exactos. Nada se hace hasta que pulsas «Confirmar», y si después cambiara un dato, la acción se bloquea." },
  { view: "chat", targets: [".sidebar a[href='#/activity']", ".tabbar a[href='#/activity']"], title: "Actividad", text: "Lo que tienes pendiente de confirmar, lo que ha hecho Vera paso a paso y un registro de auditoría que no se puede alterar." },
  { view: "chat", targets: [".sidebar a[href='#/settings']", ".tabbar a[href='#/settings']"], title: "Ajustes y botón de pánico", text: "Cambia el tema y el tono, revisa lo que recuerda de ti y, si algo no te gusta, bloquéala al instante con el botón de pánico." },
  { view: "chat", adminOnly: true, targets: [".sidebar a[href='#/admin']", ".tabbar a[href='#/admin']"], title: "Administración", text: "Altas de usuarios, modelo de IA, topes de gasto por operación y por día, y permisos de cada acción." },
  { view: "chat", targets: [".topbar .demo"], title: "Es una demostración", text: "El correo, la agenda, las reservas y los pagos son ficticios y nada sale a internet. Puedes repetir este recorrido cuando quieras desde Ajustes." },
];

const visible = (el: Element | null): el is HTMLElement => !!el && (el as HTMLElement).offsetParent !== null && el.getClientRects().length > 0;

export function startTour(opts: { isAdmin: boolean }) {
  const steps = STEPS.filter((s) => !s.adminOnly || opts.isAdmin);
  let index = 0;
  // Seen as soon as it starts, so the first-visit offer never comes back even if the tour is cut short.
  void api.updatePreferences({ tutorialDone: true }).catch(() => {});
  const previousFocus = document.activeElement as HTMLElement | null;
  const layer = document.createElement("div");
  layer.className = "tour";
  layer.innerHTML = `<div class="tour-spot" aria-hidden="true"></div>
    <div class="tour-card" role="dialog" aria-modal="true" aria-labelledby="tour-title" aria-describedby="tour-text">
      <p class="tour-count" aria-live="polite"></p><h2 id="tour-title"></h2><p id="tour-text"></p>
      <div class="tour-actions">
        <button class="btn ghost" type="button" data-tour="skip">Salir</button>
        <span class="spacer"></span>
        <button class="btn" type="button" data-tour="prev">${icon("chevron-left", "sm")} Anterior</button>
        <button class="btn primary" type="button" data-tour="next">Siguiente ${icon("chevron-right", "sm")}</button>
      </div></div>`;
  document.body.append(layer);
  const spot = layer.querySelector<HTMLElement>(".tour-spot")!;
  const card = layer.querySelector<HTMLElement>(".tour-card")!;
  const btn = (n: string) => layer.querySelector<HTMLButtonElement>(`[data-tour="${n}"]`)!;

  async function show() {
    const step = steps[index]!;
    if (step.view && !location.hash.startsWith(`#/${step.view}`)) { go(`#/${step.view}`); await new Promise((r) => setTimeout(r, 350)); }
    const target = step.targets?.map((t) => document.querySelector(t)).find(visible) ?? null;
    layer.querySelector(".tour-count")!.textContent = `Paso ${index + 1} de ${steps.length}`;
    layer.querySelector("#tour-title")!.textContent = step.title;
    layer.querySelector("#tour-text")!.innerHTML = esc(step.text);
    btn("prev").disabled = index === 0;
    btn("next").innerHTML = index === steps.length - 1 ? `${icon("check", "sm")} Terminar` : `Siguiente ${icon("chevron-right", "sm")}`;
    if (target) {
      target.scrollIntoView({ block: "nearest" });
      const r = target.getBoundingClientRect();
      Object.assign(spot.style, { display: "block", top: `${r.top - 6}px`, left: `${r.left - 6}px`, width: `${r.width + 12}px`, height: `${r.height + 12}px` });
      const below = r.bottom + 16 + card.offsetHeight < innerHeight;
      card.style.top = below ? `${Math.min(r.bottom + 16, innerHeight - card.offsetHeight - 12)}px` : `${Math.max(12, r.top - card.offsetHeight - 16)}px`;
      card.style.left = `${Math.min(Math.max(12, r.left), innerWidth - card.offsetWidth - 12)}px`;
      card.classList.remove("centered");
    } else {
      spot.style.display = "none";
      card.style.top = ""; card.style.left = "";
      card.classList.add("centered");
    }
    btn("next").focus();
  }

  async function close(completed: boolean) {
    layer.remove();
    document.removeEventListener("keydown", onKey, true);
    removeEventListener("resize", onResize);
    previousFocus?.focus?.();
    if (completed) document.dispatchEvent(new CustomEvent("tour-finished"));
  }
  const onResize = () => void show();
  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") { e.preventDefault(); void close(false); return; }
    if (e.key === "ArrowRight") { e.preventDefault(); btn("next").click(); return; }
    if (e.key === "ArrowLeft" && index > 0) { e.preventDefault(); btn("prev").click(); return; }
    if (e.key === "Tab") { // keep focus inside the card
      const items = [...card.querySelectorAll<HTMLButtonElement>("button:not([disabled])")];
      const i = items.indexOf(document.activeElement as HTMLButtonElement);
      e.preventDefault();
      items[(i + (e.shiftKey ? -1 : 1) + items.length) % items.length]!.focus();
    }
  }
  btn("skip").addEventListener("click", () => void close(false));
  btn("prev").addEventListener("click", () => { if (index > 0) { index--; void show(); } });
  btn("next").addEventListener("click", () => { if (index < steps.length - 1) { index++; void show(); } else void close(true); });
  document.addEventListener("keydown", onKey, true);
  addEventListener("resize", onResize);
  void show();
}

/** First visit: a quiet, dismissible offer in the chat rather than a modal that blocks the app. */
export function tourOffer(): string {
  return `<div class="tour-offer" role="region" aria-label="Recorrido guiado">${icon("message-square")}
    <div><strong>¿Te enseño cómo funciona Vera?</strong><p>Un recorrido de un minuto por lo importante. Puedes verlo después desde Ajustes.</p></div>
    <div class="form-actions"><button class="btn primary" type="button" data-tour-start>Empezar</button><button class="btn ghost" type="button" data-tour-dismiss>Ahora no</button></div></div>`;
}
